import { ApiError, recipientApi, type PartURL } from './api'
import { forget, recall, remember, type UploadSession } from './resume'

export type UploadState = 'queued' | 'uploading' | 'paused' | 'complete' | 'error' | 'cancelled'

export interface UploadProgress {
  state: UploadState
  /** 0..1, derived from completed parts plus the in-flight bytes of each worker. */
  progress: number
  uploadedBytes: number
  totalBytes: number
  error?: string
  fileId?: string
}

/** Parts uploaded in parallel. Four keeps a home connection saturated without
 *  starving the rest of the page. */
const CONCURRENCY = 4
/** How many presigned URLs to hold at once — refilled as the upload advances. */
const URL_WINDOW = 20
const MAX_PART_RETRIES = 4

interface UploadOptions {
  token: string
  sectionId: string
  file: File
  onProgress: (p: UploadProgress) => void
  signal: AbortSignal
}

/**
 * Uploads one file straight to S3 with multipart presigned URLs. The API is only
 * involved at the edges — begin, hand out more URLs, complete — so the bytes
 * never pass through the Go process and file size is bounded by S3, not by us.
 */
export async function uploadFile(opts: UploadOptions): Promise<string> {
  const { token, sectionId, file, onProgress, signal } = opts
  const totalBytes = file.size

  const report = (p: Partial<UploadProgress> & { state: UploadState }, uploaded: number) =>
    onProgress({
      progress: totalBytes === 0 ? 1 : Math.min(1, uploaded / totalBytes),
      uploadedBytes: uploaded,
      totalBytes,
      ...p,
    })

  report({ state: 'queued' }, 0)

  // Presigned URLs by part number, refilled in windows so a 50 GB file doesn't
  // need thousands of signatures up front.
  const urls = new Map<number, string>()
  const rememberUrls = (parts: PartURL[]) => parts.forEach((p) => urls.set(p.partNumber, p.url))

  // Parts S3 already holds. Empty on a fresh upload; on a resumed one this is
  // what saves re-sending them.
  const etags = new Map<number, string>()

  /**
   * Pick up an upload this tab started earlier, or return null if it cannot be
   * picked up. The server is the authority on what survived — partUrls both
   * proves the remembered upload is still pending and reports which parts
   * landed, so nothing here has to be taken on trust.
   */
  const resumeFrom = async (s: UploadSession) => {
    if (s.partCount < 1) return null
    const to = Math.min(s.partCount, URL_WINDOW)
    const res = await recipientApi.partUrls(token, s.fileId, 1, to)
    // A part-size change on the server moves every boundary, which would make
    // the parts already uploaded fail to line up with the slices sent next.
    if (res.partCount !== s.partCount) return null
    rememberUrls(res.parts)
    for (const p of res.uploadedParts) {
      if (p.partNumber >= 1 && p.partNumber <= s.partCount) etags.set(p.partNumber, p.etag)
    }
    return { ...s, fetchedThrough: to }
  }

  let session: (UploadSession & { fetchedThrough: number }) | null = null
  const saved = recall(token, sectionId, file)
  if (saved) {
    try {
      session = await resumeFrom(saved)
    } catch (err) {
      // 4xx means the remembered upload is gone — completed, aborted, swept, or
      // the link has closed. Start over. Anything else is a real failure and
      // must not be swallowed into a silent restart.
      if (!(err instanceof ApiError && err.status < 500)) throw err
    }
    if (!session) forget(token, sectionId, file)
  }

  if (!session) {
    const begin = await recipientApi.beginUpload(token, sectionId, {
      filename: file.name,
      sizeBytes: file.size,
      contentType: file.type || 'application/octet-stream',
    })
    rememberUrls(begin.parts)
    session = {
      fileId: begin.fileId,
      uploadId: begin.uploadId,
      partSize: begin.partSize,
      partCount: begin.partCount,
      fetchedThrough: begin.parts.length,
    }
    remember(token, sectionId, file, session)
  }

  const { fileId, partSize, partCount } = session
  let fetchedThrough = session.fetchedThrough

  const ensureUrls = async (upTo: number) => {
    if (upTo <= fetchedThrough || fetchedThrough >= partCount) return
    const from = fetchedThrough + 1
    const to = Math.min(partCount, from + URL_WINDOW - 1)
    const res = await recipientApi.partUrls(token, fileId, from, to)
    rememberUrls(res.parts)
    fetchedThrough = to
  }

  // Every part is partSize except the last, which is whatever is left over.
  const sizeOfPart = (n: number) =>
    Math.max(0, Math.min(n * partSize, file.size) - (n - 1) * partSize)

  // Bytes finished per part, so progress moves smoothly during a part rather
  // than jumping by a whole part at a time.
  const inFlight = new Map<number, number>()
  // Resumed parts count as done, so the bar picks up where it left off instead
  // of restarting at zero and then leaping forward.
  let completedBytes = 0
  for (const n of etags.keys()) completedBytes += sizeOfPart(n)

  const emit = (state: UploadState = 'uploading') => {
    let live = 0
    for (const n of inFlight.values()) live += n
    report({ state, fileId }, completedBytes + live)
  }

  emit()

  let nextPart = 1
  let failure: Error | null = null

  const worker = async () => {
    while (!failure) {
      if (signal.aborted) return
      const partNumber = nextPart++
      if (partNumber > partCount) return
      // Already in S3 from an earlier attempt at this file.
      if (etags.has(partNumber)) continue

      await ensureUrls(partNumber)
      const url = urls.get(partNumber)
      if (!url) throw new Error(`no upload URL for part ${partNumber}`)

      const start = (partNumber - 1) * partSize
      const blob = file.slice(start, Math.min(start + partSize, file.size))

      try {
        const etag = await putPart(url, blob, signal, (loaded) => {
          inFlight.set(partNumber, loaded)
          emit()
        })
        etags.set(partNumber, etag)
        inFlight.delete(partNumber)
        completedBytes += blob.size
        emit()
      } catch (err) {
        inFlight.delete(partNumber)
        if (signal.aborted) return
        failure = err instanceof Error ? err : new Error(String(err))
        return
      }
    }
  }

  try {
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, partCount) }, worker))
  } catch (err) {
    failure = err instanceof Error ? err : new Error(String(err))
  }

  if (signal.aborted) {
    // Give back the reserved quota; ignore failures, the sweeper is the backstop.
    forget(token, sectionId, file)
    await recipientApi.abortUpload(token, fileId).catch(() => {})
    report({ state: 'cancelled', fileId }, completedBytes)
    throw new DOMException('upload cancelled', 'AbortError')
  }

  if (failure) {
    // The upload is aborted here rather than left resumable on purpose: a part
    // that failed four times with backoff is not a blip, and leaving the
    // reservation in place would hold quota the recipient cannot use. Dropping
    // the record keeps a retry from resuming an upload that no longer exists.
    forget(token, sectionId, file)
    await recipientApi.abortUpload(token, fileId).catch(() => {})
    report({ state: 'error', error: failure.message, fileId }, completedBytes)
    throw failure
  }

  const parts = Array.from(etags.entries())
    .map(([partNumber, etag]) => ({ partNumber, etag }))
    .sort((a, b) => a.partNumber - b.partNumber)

  await recipientApi.completeUpload(token, fileId, parts)
  forget(token, sectionId, file)
  report({ state: 'complete', fileId }, totalBytes)
  return fileId
}

/**
 * XHR rather than fetch: it is the only way to observe upload progress in the
 * browser today, and progress on multi-GB parts is the whole point.
 */
function putPart(
  url: string,
  blob: Blob,
  signal: AbortSignal,
  onProgress: (loaded: number) => void,
  attempt = 1,
): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url, true)

    const abort = () => xhr.abort()
    signal.addEventListener('abort', abort, { once: true })
    const cleanup = () => signal.removeEventListener('abort', abort)

    xhr.upload.onprogress = (e) => onProgress(e.loaded)

    xhr.onload = () => {
      cleanup()
      if (xhr.status >= 200 && xhr.status < 300) {
        // S3 identifies the part by its ETag; without it the upload can't complete.
        const etag = xhr.getResponseHeader('ETag')
        if (!etag) {
          reject(new Error('S3 did not return an ETag — check the bucket CORS ExposeHeaders'))
          return
        }
        resolve(etag)
        return
      }
      reject(new Error(`part upload failed with HTTP ${xhr.status}`))
    }

    xhr.onerror = () => {
      cleanup()
      reject(new Error('network error during part upload'))
    }
    xhr.onabort = () => {
      cleanup()
      reject(new DOMException('aborted', 'AbortError'))
    }

    xhr.send(blob)
  }).catch(async (err) => {
    // Transient network trouble is expected on long uploads; retry the part with
    // backoff before failing the whole file.
    if (signal.aborted || attempt >= MAX_PART_RETRIES) throw err
    await new Promise((r) => setTimeout(r, 500 * 2 ** (attempt - 1)))
    onProgress(0)
    return putPart(url, blob, signal, onProgress, attempt + 1)
  })
}
